// src/pages/TripPlanner.tsx
// src/pages/TripPlanner.tsx
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Stepper, 
  Step, 
  StepLabel, 
  Button, 
  Typography, 
  Tabs, 
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import { 
  TravelExplore as TravelIcon, 
  Hotel as HotelIcon, 
  LocalActivity as ActivityIcon, 
  Share as ShareIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AutoAwesome as MagicIcon,
  Email as EmailIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { v4 as uuidv4 } from 'uuid';

// Import components
import EnhancedTripForm from './components/EnhancedTripForm';
import ItineraryGenerator from './components/ItineraryGenerator';
import SuggestionList, { generateMockSuggestions } from './components/SuggestionList';
import MapVisualization from './MapVisualization';
import AIAssistant from './AIAssistant';
import { TripPlannerProvider, useTripPlanner } from './TripPlannerContext';

const steps = ['Trip Details', 'Generate Itinerary', 'Refine Activities', 'Finalize & Share'];

// Define interfaces for the component
interface Trip {
  id: string;
  title: string;
  description: string;
  destination: string;
  startDate: Date;
  endDate: Date;
  purpose: string;
  style: string;
  pace: string;
  budget: number[];
  groupSize: number;
  groupRelationship: string;
  interests: string[];
  specialOccasion?: string;
  itinerary?: any[];
  [key: string]: any;
}

interface ItineraryActivity {
  id: string;
  title: string;
  description: string;
  time: string;
  location: string;
  type: 'activity' | 'food' | 'accommodation' | 'transportation';
  cost?: number;
  duration?: string;
  notes?: string;
  isCustom?: boolean;
}

const TripPlannerContent = () => {
  const { tripPlan, addActivity } = useTripPlanner();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [createTripDialogOpen, setCreateTripDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch existing trips on component mount
  useEffect(() => {
    const fetchTrips = async () => {
      try {
        const tripsCollection = collection(db, 'trips');
        const tripSnapshot = await getDocs(tripsCollection);
        
        const tripData: Trip[] = [];
        
        tripSnapshot.forEach((doc) => {
          const data = doc.data();
          
          tripData.push({
            id: doc.id,
            title: data.title,
            description: data.description || '',
            destination: data.destination,
            startDate: data.startDate.toDate(),
            endDate: data.endDate.toDate(),
            purpose: data.purpose || '',
            style: data.style || '',
            pace: data.pace || '',
            budget: data.budget || [100, 500],
            groupSize: data.groupSize || 2,
            groupRelationship: data.groupRelationship || '',
            interests: data.interests || [],
            specialOccasion: data.specialOccasion || '',
            itinerary: data.itinerary || []
          });
        });
        
        // Sort by start date (newest first)
        setTrips(tripData.sort((a, b) => b.startDate.getTime() - a.startDate.getTime()));
        setLoading(false);
      } catch (error) {
        console.error('Error fetching trips:', error);
        setLoading(false);
      }
    };
    
    fetchTrips();
  }, []);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleGenerateSuggestions = () => {
    if (selectedTrip) {
      setIsLoadingSuggestions(true);
      
      // Simulate API call delay
      setTimeout(() => {
        setSuggestions(generateMockSuggestions(selectedTrip.destination));
        setIsLoadingSuggestions(false);
      }, 1500);
    }
  };

  const handleAddSuggestion = (suggestion: any) => {
    // Transform suggestion into activity
    const activity = {
      id: suggestion.id,
      title: suggestion.title,
      description: suggestion.description,
      date: new Date(), // This would need proper date assignment
      location: suggestion.type === 'food' ? suggestion.address : suggestion.location,
      coordinates: suggestion.coordinates || [0, 0],
      tags: suggestion.tags,
    };
    
    addActivity(activity);
  };

  const handleCreateTrip = async (tripData: any) => {
     try {
        // Convert dates to Firestore timestamps
        const firestoreData = {
           ...tripData,
           startDate: Timestamp.fromDate(tripData.startDate),
           endDate: Timestamp.fromDate(tripData.endDate),
           createdAt: Timestamp.now(),
        };

        // If editing existing trip
        if (selectedTrip && selectedTrip.id) {
           await updateDoc(doc(db, 'trips', selectedTrip.id), firestoreData);

           // Update local state
           setTrips(
              trips.map((trip) => (trip.id === selectedTrip.id ? { ...trip, ...tripData } : trip))
           );

           setSelectedTrip({ ...selectedTrip, ...tripData });
        } else {
           // Create new trip using backend API
           const { createApiUrl } = await import('../../utils/apiConfig');
           const { getAuthHeaders } = await import('../../utils/auth');
           const headers = await getAuthHeaders();
           const apiUrl = createApiUrl('/api/trips');

           const response = await fetch(apiUrl, {
              method: 'POST',
              headers,
              body: JSON.stringify({
                 title: tripData.title,
                 description: tripData.description || '',
                 startDate: tripData.startDate.toISOString(),
                 endDate: tripData.endDate.toISOString(),
                 isPublic: tripData.isPublic || false,
                 primaryDestination: tripData.primaryDestination || tripData.destination,
                 coverPhotoUrl: tripData.coverPhotoUrl,
                 userId: tripData.userId,
              }),
           });

           const result = await response.json();

           if (!response.ok) {
              throw new Error(result.error || 'Failed to create trip');
           }

           // Add to local state
           const newTrip = {
              id: result.tripId,
              ...tripData,
           };

           setTrips([newTrip, ...trips]);
           setSelectedTrip(newTrip);
        }

        setCreateTripDialogOpen(false);
        // Move to next step if creating new trip
        if (!selectedTrip) {
           setActiveStep(1);
        }
     } catch (error) {
        console.error('Error saving trip:', error);
     }
  };

  const handleDeleteTrip = async () => {
     if (!selectedTrip) return;

     try {
        // Use backend API to delete trip
        const { createApiUrl } = await import('../../utils/apiConfig');
        const { getAuthHeaders } = await import('../../utils/auth');
        const headers = await getAuthHeaders();
        const apiUrl = createApiUrl(
           `/api/trips/${selectedTrip.id}?userId=${selectedTrip.userId || 'current-user-id'}`
        );

        const response = await fetch(apiUrl, {
           method: 'DELETE',
           headers,
        });

        const result = await response.json();

        if (!response.ok) {
           throw new Error(result.error || 'Failed to delete trip');
        }

        // Update local state
        setTrips(trips.filter((trip) => trip.id !== selectedTrip.id));
        setSelectedTrip(null);
        setDeleteDialogOpen(false);
        setActiveStep(0);
     } catch (error) {
        console.error('Error deleting trip:', error);
     }
  };

  const handleSaveItinerary = async (itinerary: any[]) => {
    if (!selectedTrip) return;
    
    try {
      // Save itinerary to the trip document
      await updateDoc(doc(db, 'trips', selectedTrip.id), {
        itinerary: itinerary
      });
      
      // Update local state
      setSelectedTrip({
        ...selectedTrip,
        itinerary
      });
      
      setTrips(trips.map(trip => 
        trip.id === selectedTrip.id ? {...trip, itinerary} : trip
      ));
      
      // Move to next step
      handleNext();
    } catch (error) {
      console.error('Error saving itinerary:', error);
    }
  };

  const handleAddActivity = (activity: ItineraryActivity, day: Date) => {
    // In a real app, you would update the activity in Firestore

  };

  const handleEditTrip = (trip: Trip) => {
    setSelectedTrip(trip);
    setCreateTripDialogOpen(true);
  };

  const handleSelectTrip = (trip: Trip) => {
    setSelectedTrip(trip);
    setActiveStep(1); // Move to itinerary generation step
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Trip Selection or Creation */}
      {!selectedTrip && activeStep === 0 && (
        <Box sx={{ mb: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5">Plan Your Trip</Typography>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={() => setCreateTripDialogOpen(true)}
              >
                Create New Trip
              </Button>
            </Box>
            
            {trips.length > 0 ? (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  Your Trips
                </Typography>
                <List>
                  {trips.map((trip) => (
                    <Paper 
                      key={trip.id} 
                      elevation={1} 
                      sx={{ mb: 2, p: 2, borderRadius: 2, cursor: 'pointer' }}
                      onClick={() => handleSelectTrip(trip)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="h6">{trip.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {trip.destination} • {new Date(trip.startDate).toLocaleDateString()} to {new Date(trip.endDate).toLocaleDateString()}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Chip 
                              label={trip.purpose || 'Leisure'} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                            <Chip 
                              label={`${trip.groupSize} ${trip.groupSize === 1 ? 'Person' : 'People'}`}
                              size="small" 
                              color="secondary" 
                              variant="outlined"
                            />
                            {trip.specialOccasion && (
                              <Chip 
                                label={trip.specialOccasion} 
                                size="small" 
                                color="success" 
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                        <Box>
                          <IconButton 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditTrip(trip);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTrip(trip);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </List>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary" paragraph>
                  You don't have any trips planned yet.
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />}
                  onClick={() => setCreateTripDialogOpen(true)}
                >
                  Create Your First Trip
                </Button>
              </Box>
            )}
          </Paper>
        </Box>
      )}
      
      {(selectedTrip || activeStep > 0) && (
        <>
          {/* Trip Planner Interface */}
          <Box sx={{ display: 'flex', height: selectedTrip ? 'calc(100vh - 220px)' : 'calc(100vh - 84px)' }}>
            {/* Left Panel: Planning Steps */}
            <Box sx={{ width: '70%', pr: 2, display: 'flex', flexDirection: 'column' }}>
              {selectedTrip && (
                <Paper sx={{ p: 3, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h5">{selectedTrip.title}</Typography>
                      <Typography variant="body1" color="text.secondary">
                        {selectedTrip.destination} • {new Date(selectedTrip.startDate).toLocaleDateString()} to {new Date(selectedTrip.endDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Stepper activeStep={activeStep} sx={{ width: '60%' }}>
                      {steps.map((label) => (
                        <Step key={label}>
                          <StepLabel>{label}</StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </Box>
                </Paper>
              )}

              <Paper sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                  {activeStep === 0 && (
                    <EnhancedTripForm 
                      onSubmit={handleCreateTrip} 
                      initialData={selectedTrip || {}}
                      existingTrips={trips}
                    />
                  )}
                  
                  {activeStep === 1 && selectedTrip && (
                    <ItineraryGenerator 
                      tripDetails={selectedTrip}
                      onSaveItinerary={handleSaveItinerary}
                      onAddActivity={handleAddActivity}
                      existingItinerary={selectedTrip.itinerary || []}
                    />
                  )}
                  
                  {activeStep === 2 && (
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Refine Activities
                      </Typography>
                      {/* Activity refinement component would go here */}
                      <Typography variant="body1" paragraph>
                        Fine-tune your activities, add transportation, and adjust timing.
                      </Typography>
                      
                      {selectedTrip?.itinerary && selectedTrip.itinerary.length > 0 ? (
                        <Box>
                          {/* Display itinerary days and allow editing */}
                          {selectedTrip.itinerary.map((day, index) => (
                            <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                              <Typography variant="subtitle1">
                                Day {index + 1}: {new Date(day.date).toLocaleDateString()}
                              </Typography>
                              <List dense>
                                {day.activities.map((activity: any) => (
                                  <ListItem key={activity.id}>
                                    <ListItemText
                                      primary={activity.title}
                                      secondary={`${activity.time} • ${activity.location}`}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Paper>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No itinerary generated yet. Please go back to the previous step.
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {activeStep === 3 && (
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Finalize & Share Your Trip
                      </Typography>
                      
                      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          Trip Summary
                        </Typography>
                        
                        {selectedTrip && (
                          <Box>
                            <Typography variant="h6">{selectedTrip.title}</Typography>
                            <Typography variant="body1">
                              {selectedTrip.destination}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                              {new Date(selectedTrip.startDate).toLocaleDateString()} to {new Date(selectedTrip.endDate).toLocaleDateString()}
                              {' • '}{selectedTrip.groupSize} people
                              {selectedTrip.specialOccasion && ` • ${selectedTrip.specialOccasion}`}
                            </Typography>
                            
                            <Divider sx={{ my: 2 }} />
                            
                            <Typography variant="subtitle2" gutterBottom>
                              Your trip includes:
                            </Typography>
                            
                            {selectedTrip.itinerary && (
                              <Typography variant="body2">
                                • {selectedTrip.itinerary.length} days planned
                              </Typography>
                            )}
                            
                            <Typography variant="body2">
                              • {selectedTrip.itinerary?.reduce((total: number, day: any) => total + day.activities.length, 0) || 0} activities
                            </Typography>
                            
                            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                              <Button 
                                variant="contained" 
                                startIcon={<EmailIcon />}
                              >
                                Email Itinerary
                              </Button>
                              <Button 
                                variant="outlined"
                                startIcon={<ShareIcon />}
                              >
                                Share Trip
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </Paper>
                    </Box>
                  )}
                </Box>

                {selectedTrip && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
                    <Button
                      disabled={activeStep === 0}
                      onClick={handleBack}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={activeStep === steps.length - 1 || (activeStep === 1 && (!selectedTrip?.itinerary || selectedTrip.itinerary.length === 0))}
                    >
                      {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
                    </Button>
                  </Box>
                )}
              </Paper>
            </Box>

            {/* Right Panel: Map, AI Assistant, and Suggestions */}
            <Box sx={{ width: '30%', display: 'flex', flexDirection: 'column' }}>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                  <Tab label="Map" icon={<TravelIcon />} />
                  <Tab label="AI Assistant" />
                  <Tab label="Suggestions" />
                </Tabs>
              </Paper>

              <Paper sx={{ p: 0, flexGrow: 1, overflow: 'hidden' }}>
                {tabValue === 0 ? (
                  <MapVisualization />
                ) : tabValue === 1 ? (
                  <AIAssistant />
                ) : (
                  <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
                    <SuggestionList
                      suggestions={suggestions}
                      destination={selectedTrip?.destination}
                      isLoading={isLoadingSuggestions}
                      onAddSuggestion={handleAddSuggestion}
                      onGenerateSuggestions={handleGenerateSuggestions}
                      title="AI-Powered Suggestions"
                      emptyMessage={selectedTrip
                        ? "Click 'Get Suggestions' to get AI recommendations for your trip." 
                        : "Select a destination first to get suggestions."}
                    />
                  </Box>
                )}
              </Paper>
            </Box>
          </Box>
        </>
      )}
      
      {/* Create/Edit Trip Dialog */}
      <Dialog 
        open={createTripDialogOpen} 
        onClose={() => setCreateTripDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedTrip && createTripDialogOpen ? 'Edit Trip' : 'Create New Trip'}
        </DialogTitle>
        <DialogContent>
          <EnhancedTripForm 
            onSubmit={handleCreateTrip} 
            initialData={selectedTrip && createTripDialogOpen ? selectedTrip : {}}
            existingTrips={trips}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateTripDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete "{selectedTrip?.title}"?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteTrip} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const TripPlanner: React.FC = () => {
  return (
    <TripPlannerProvider>
      <TripPlannerContent />
    </TripPlannerProvider>
  );
};

export default TripPlanner;